import { Schema } from "effect";
import { t } from "../i18n/en";

const APP_NAME = "Harness";
export const CANONICAL_SITE_URL = "https://harnessmyhd.com";
const SHARE_IMAGE_URL = "https://harnessmyhd.com/og/harness-share.png";
const DEFAULT_DESCRIPTION = t("ui.copy_explore_human_design_charts_awarenes_5190f783");

const SeoOptionsSchema = Schema.Struct({
  description: Schema.optional(Schema.String),
  canonicalPath: Schema.optional(Schema.String),
  image: Schema.optional(Schema.String),
  imageAlt: Schema.optional(Schema.String),
  type: Schema.optional(Schema.Literals(["website", "article"])),
  robots: Schema.optional(Schema.String),
});

export type SeoOptions = typeof SeoOptionsSchema.Type;

export function pageTitle(title: string) {
  if (title === APP_NAME) {
    return APP_NAME;
  }

  return `${title} · ${APP_NAME}`;
}

export function pageMeta(title: string, options: SeoOptions = {}) {
  const fullTitle = pageTitle(title);
  const seo = Schema.decodeSync(SeoOptionsSchema)(options);
  const description = seo.description || DEFAULT_DESCRIPTION;
  const image = seo.image || SHARE_IMAGE_URL;
  const imageAlt = seo.imageAlt || "Harness — an awareness raising platform";
  const canonicalUrl = seo.canonicalPath
    ? new URL(seo.canonicalPath, CANONICAL_SITE_URL).toString()
    : undefined;

  return [
    { title: fullTitle },
    { name: "description", content: description },
    { property: "og:title", content: fullTitle },
    { property: "og:description", content: description },
    { property: "og:type", content: seo.type || "website" },
    { property: "og:site_name", content: APP_NAME },
    ...(canonicalUrl ? [{ property: "og:url", content: canonicalUrl }] : []),
    { property: "og:image", content: image },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:alt", content: imageAlt },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: fullTitle },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: image },
    ...(seo.robots ? [{ name: "robots", content: seo.robots }] : []),
  ];
}

export function pageHead(title: string, options: SeoOptions = {}) {
  const meta = pageMeta(title, options);
  const canonicalPath = options.canonicalPath;

  return {
    meta,
    ...(canonicalPath
      ? {
          links: [
            { rel: "canonical", href: new URL(canonicalPath, CANONICAL_SITE_URL).toString() },
          ],
        }
      : {}),
  };
}

export function formatPageIdentifier(identifier: string) {
  return decodeURIComponent(identifier)
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}
